import React, { useState, useRef } from "react";
import { Box, Typography, Tabs, Tab, Alert, Button } from "@mui/material";
import { PathFundSelection } from "../../services/MimshakProductUtil";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import MimshakScatterCharts from "./MimshakScatterCharts";
import SuggestedPathSelection from "./SuggestedPathSelection";
import { PolicyExposureChanges } from "../../hooks/feeLab/usePathDragChanges";
import { PreferenceDistributionData } from "../../interfaces/DTO/contactPreferences";
import StyledDrawer from "../../shared/UIParts/StyledDrawer";
import SavingsSummary from "../PersonFinancialData/SavingsSummary";
import { colorHandlerCode, colorAdjustedProductType } from "../../utils/colorEnumFormatter";
import { formatHandlerCode, formatCurrency, formatPercentage } from "../../utils/namesFormater";
import { formatAdjustedProductType, formatProductType } from "../../utils/enumFormatter";

interface Props {
  open: boolean;
  onClose: () => void;
  mimshak: MimshakData;
  originalMimshak?: MimshakData;
  paths?: Array<{
    pathCode: number;
    pathName: string;
    amount: number;
    depositPercentage?: number;
    fund: FundDataBasic | null;
  }>;
  originalFund?: FundDataBasic | null;
  onFundSelected?: (fund: FundDataBasic, originalPathCode?: number) => void;
  onFundsSelected?: (selections: PathFundSelection[]) => void;
  onReset?: () => void;
  title?: string;
  exposureChanges?: PolicyExposureChanges;
  preferenceData?: PreferenceDistributionData;
  /** when opened from a dialog (a dialog is above a drawer by default) */
  zIndex?: number;
  /** splitting into several paths - each chosen fund is added as another path */
  startInAddMode?: boolean;
}

const SelectNewPathDrawer: React.FC<Props> = ({
  open,
  onClose,
  mimshak,
  originalMimshak,
  paths = [],
  originalFund = null,
  onFundSelected,
  onFundsSelected,
  onReset,
  title,
  exposureChanges,
  preferenceData,
  zIndex,
  startInAddMode,
}) => {
  const [activeTab, setActiveTab] = useState(1);
  const [closeError, setCloseError] = useState<string | null>(null);
  const validateRef = useRef<(() => string | null) | null>(null);
  const fixRef = useRef<(() => void) | null>(null);
  const collectRef = useRef<(() => PathFundSelection[] | null) | null>(null);

  const handleFundSelected = (policyNumber: string, selectedFund: FundDataBasic, originalPathCode?: number) => {
    if (onFundSelected) {
      onFundSelected(selectedFund, originalPathCode);
    }
  };

const validateSnapshot = (selections: PathFundSelection[]): string | null => {
    const total = selections.reduce((s, p) => s + p.amount, 0);
    const expected = mimshak.funds_sum ?? 0;
    if (expected > 0 && Math.round(total) !== Math.round(expected)) {
      return `סכום המסלולים (${Math.round(total).toLocaleString()}) אינו תואם לסכום הפוליסה (${Math.round(expected).toLocaleString()})`;
    }
    if (selections.length > 1) {
      const depositTotal = selections.reduce((s, p) => s + (p.depositPercentage ?? 0), 0);
      if (depositTotal > 0 && Math.round(depositTotal) !== 100) {
        return `חלוקת ההפקדה מסתכמת ל-${depositTotal.toFixed(1)}% במקום 100%`;
      }
    }
    return null;
  };

  const handleClose = () => {
    const error = validateRef.current?.() ?? null;
    if (error) { setCloseError(error); return; }
    const selections = collectRef.current?.() ?? null;
    if (selections && selections.length > 0) {
      const snapshotError = validateSnapshot(selections);
      if (snapshotError) { setCloseError(snapshotError); return; }
      onFundsSelected?.(selections);
    }
    setCloseError(null);
    onClose();
  };

  // Current handler code (where it's going to) - used for filtering funds
  const currentHandlerCode = mimshak.handler_code;

  return (
    <StyledDrawer open={open} onClose={handleClose} onBack={handleClose} width={Math.round(window.innerWidth * 0.7)} zIndex={zIndex}>
      <Box>
        {title && (
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
        )}
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Box width={12} height={12} borderRadius="30%" bgcolor={colorHandlerCode(mimshak.handler_code)} />
          <Typography variant="subtitle1" fontWeight="bold">{formatHandlerCode(mimshak.handler_code)}</Typography>
          <Typography variant="subtitle1" color="text.secondary">·</Typography>
          <Box
            sx={{
              backgroundColor: colorAdjustedProductType(mimshak.adjusted_product_type ?? mimshak.product_type),
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '0.8rem',
              px: 1,
              py: 0.25,
              borderRadius: 1,
            }}
          >
            {formatAdjustedProductType(mimshak.adjusted_product_type ?? mimshak.product_type)}
          </Box>
          <Typography variant="subtitle1" color="text.secondary">·</Typography>
          {originalMimshak && originalMimshak.funds_sum !== mimshak.funds_sum ? (
            <Typography variant="subtitle1">
              <Box component="span" sx={{ color: "text.secondary" }}>{formatCurrency(originalMimshak.funds_sum)}</Box>
              {" ← "}
              <Box component="span" sx={{ fontWeight: 700 }}>{formatCurrency(mimshak.funds_sum)}</Box>
            </Typography>
          ) : (
            <Typography variant="subtitle1">{formatCurrency(mimshak.funds_sum)}</Typography>
          )}
          <Typography variant="subtitle1" color="text.secondary">·</Typography>
          <Box sx={{ display: "inline-flex" }}>
            <SavingsSummary policies={(mimshak.data?.products ?? []).flatMap(p => p.policies_or_accounts ?? [])} />
          </Box>
        </Box>

        {/* מסלולים מקוריים */}
        {(paths.length > 0 || originalFund) && (
          <Box mt={0.5} mb={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              {paths.length === 1 ? 'מסלול מקורי:' : 'מסלולים מקוריים:'}
            </Typography>
            {paths.length > 0
              ? (() => {
                  const totalAmount = paths.reduce((s, p) => s + p.amount, 0);
                  const showPct = paths.length > 1 && totalAmount > 0;
                  return paths.map((p) => p.fund && (
                  <Box key={p.pathCode} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                    <Typography variant="body2" fontWeight="bold">{p.fund.fund_name}{showPct && p.amount > 0 ? ` (${(p.amount / totalAmount * 100).toFixed(1)}%)` : ''}</Typography>
                    {paths.length > 1 && p.amount > 0 && <Typography variant="body2" color="text.secondary">| {formatCurrency(p.amount)}</Typography>}
                    {p.fund.sub_specialization && <Typography variant="body2" color="text.secondary">| {p.fund.sub_specialization}</Typography>}
                    {p.fund.stock_market_percent != null && <Typography variant="body2" color="text.secondary">| מניות: {formatPercentage(p.fund.stock_market_percent)}</Typography>}
                    {p.fund.foreign_currency_exposure_percent != null && <Typography variant="body2" color="text.secondary">| מט"ח: {formatPercentage(p.fund.foreign_currency_exposure_percent)}</Typography>}
                    {p.fund.foreign_exposure_percent != null && <Typography variant="body2" color="text.secondary">| חו"ל: {formatPercentage(p.fund.foreign_exposure_percent)}</Typography>}
                  </Box>
                ));
                })()
              : originalFund && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                    <Typography variant="body2" fontWeight="bold">{originalFund.fund_name}</Typography>
                    {originalFund.sub_specialization && <Typography variant="body2" color="text.secondary">| {originalFund.sub_specialization}</Typography>}
                    {originalFund.stock_market_percent != null && <Typography variant="body2" color="text.secondary">| מניות: {formatPercentage(originalFund.stock_market_percent)}</Typography>}
                    {originalFund.foreign_currency_exposure_percent != null && <Typography variant="body2" color="text.secondary">| מט"ח: {formatPercentage(originalFund.foreign_currency_exposure_percent)}</Typography>}
                    {originalFund.foreign_exposure_percent != null && <Typography variant="body2" color="text.secondary">| חו"ל: {formatPercentage(originalFund.foreign_exposure_percent)}</Typography>}
                  </Box>
                )
            }
          </Box>
        )}

        {closeError && (
          <Alert
            severity="error"
            onClose={() => setCloseError(null)}
            sx={{ mb: 1, fontSize: '1.1rem' }}
            action={
              <Box display="flex" gap={1} mr={3}>
                <Button variant="contained" color="success"  onClick={() => { fixRef.current?.(); setCloseError(null); }}>
                  תסדר לי
                </Button>
                <Button variant="contained" color="error"  onClick={() => { setCloseError(null); onClose(); }}>
                  בטל שינויים וסגור
                </Button>
              </Box>
            }
          >
            {closeError}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
          <Tab label="גרף השוואה" />
          <Tab label="מסלולים מומלצים" />
        </Tabs>

        {activeTab === 0 && (
          <MimshakScatterCharts
            mimshakData={[mimshak]}
            title={title}
            onFundSelected={onFundSelected}
            initialFilters={{
              handlers: [String(currentHandlerCode)],
              displayPreferences: {
                filterPopulation: false,
                colorBy: null,
                pinnedItems: {
                  subSpecializations: paths.length > 0
                    ? Array.from(new Set(paths.map(p => p.fund?.sub_specialization).filter(Boolean) as string[]))
                    : (originalFund?.sub_specialization ? [originalFund.sub_specialization] : []),
                },
              },
            }}
          />
        )}

        {activeTab === 1 && (
          <>
            <SuggestedPathSelection
              mimshak={mimshak}
              originalMimshak={originalMimshak}
              onFundSelected={handleFundSelected}
              onReset={onReset && (!originalMimshak || String(mimshak.handler_code) === String(originalMimshak.handler_code)) ? () => { onReset(); onClose(); } : undefined}
              onFundsSelected={onFundsSelected ? (_, selections) => {
                if (selections.length > 0) {
                  // single-path immediate: validate snapshot before accepting
                  const error = validateSnapshot(selections);
                  if (error) { setCloseError(error); return; }
                  onFundsSelected(selections);
                  setCloseError(null);
                  onClose();
                } else {
                  // multi-path: collect via ref and validate
                  handleClose();
                }
              } : undefined}
              exposureChanges={exposureChanges}
              preferenceData={preferenceData}
              onValidate={(fn) => { validateRef.current = fn; }}
              onFix={(fn) => { fixRef.current = fn; }}
              onCollect={(fn) => { collectRef.current = fn; }}
              startInAddMode={startInAddMode}
            />
          </>
        )}

      </Box>
    </StyledDrawer>
  );
};

export default SelectNewPathDrawer;
